import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { FC, useContext, useMemo, useState } from 'react';
import { IResponse, ISortDirection, IUnderContract } from '../../../models';
import { useQuery } from 'react-query';
import { MobileTable, Pagination, Table } from '../../../components';
import { getUnderContract } from '../../../fetch';
import { useHistory } from 'react-router-dom';
import { Edit } from '@mui/icons-material';
import {
  formatEllipsis,
  formatDate,
  formatShortFriendlyDateWithTime,
  isTextOverflowing,
  getEnvironment,
  SALES_PORTAL_PER_PAGE_OPTIONS,
  handleSortDirection
} from '../../../helpers';
import { TablePopover } from '../../../components/table/TablePopover';
import { UnderContractFilters } from './under-contract-filters';
import { SalesContext, update } from '../../../context/sales';
import { GlobalContext } from '../../../context/global';
import {
  filtersPayload,
  sortingPayload,
  updateFilters,
  updateSort
} from '../../../context/actions';
import { theme } from '../../../styles/theme';

const PAGE_NAME = 'under-contract';

interface IUnderContractFilters {
  filters?: {
    neighborhoodIds?: string[];
    lotNumber?: string;
    search?: string;
    closingDateLocked?: boolean;
    showHiddenBuilds?: boolean;
    statuses?: string[];
  };
}

interface IUnderContractSorting {
  sort?: string;
  sortDirection?: { [key: string]: ISortDirection };
}

export const UnderContractList = ({ showHiddenBuilds, setShowHiddenBuilds }) => {
  const isMobile = useMediaQuery('(max-width: 960px)');
  const removeStickyHeader = useMediaQuery('(max-width: 1850px)');
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );

  const { filterValues, dispatchFilters, sortingValues, dispatchSorting } =
    useContext(GlobalContext);

  const currentFilterState = filterValues.find(state => state?.pageName === PAGE_NAME);

  const persistentFilters = useMemo<IUnderContractFilters>(
    () =>
      currentFilterState
        ? (currentFilterState as IUnderContractFilters)
        : ({} as IUnderContractFilters),
    [currentFilterState]
  );

  const currentSortingState = sortingValues.find(state => state?.pageName === PAGE_NAME);

  const persistentSorting = useMemo<IUnderContractSorting>(
    () =>
      currentSortingState
        ? (currentSortingState as IUnderContractSorting)
        : ({} as IUnderContractSorting),
    [currentSortingState]
  );

  const [selectedNeighborhoodIds, setSelectedNeighborhoodIds] = useState<string[]>(
    persistentFilters?.filters?.neighborhoodIds ?? []
  );
  const [selectedLotId, setSelectedLotId] = useState<string>(
    persistentFilters?.filters?.lotNumber ?? ''
  );
  const [closingDateLocked, setClosingDateLocked] = useState<boolean | undefined>(
    persistentFilters?.filters?.closingDateLocked ?? undefined
  );
  const [searchedValue, setSearchedValue] = useState<string>(
    persistentFilters?.filters?.search ?? ''
  );
  const [searchValue, setSearchValue] = useState<string>(persistentFilters?.filters?.search ?? '');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    persistentFilters?.filters?.statuses ?? []
  );

  const selectedSort = useMemo<string>(
    () => persistentSorting.sort ?? 'EstimatedClosingDate',
    [persistentSorting.sort]
  );

  const sortDirection = useMemo<{
    SageId?: ISortDirection;
    NeighborhoodName?: ISortDirection;
    LotNumber?: ISortDirection;
    StreetAddress?: ISortDirection;
    Superintendent?: ISortDirection;
    ClientName?: ISortDirection;
    LenderName?: ISortDirection;
    LenderContactName?: ISortDirection;
    LoanType?: ISortDirection;
    EstimatedClosingDate?: ISortDirection;
    ActualClosingDate?: ISortDirection;
    ClosingNotes?: ISortDirection;
    HomeownerOrientation?: ISortDirection;
    HomeownerCelebration?: ISortDirection;
  }>(
    () =>
      persistentSorting?.sortDirection && Object.keys(persistentSorting?.sortDirection).length > 0
        ? persistentSorting?.sortDirection
        : {
            EstimatedClosingDate: 'Asc'
          },
    [persistentSorting.sortDirection]
  );

  const { isLoading, data: underContractData } = useQuery<IResponse<IUnderContract[]>, Error>(
    [
      'getUnderContract',
      page,
      perPage,
      selectedSort,
      sortDirection,
      persistentFilters,
      searchedValue,
      showHiddenBuilds
    ],
    async () => {
      return await getUnderContract({
        page: page + 1,
        perPage,
        neighborhoodIds: persistentFilters?.filters?.neighborhoodIds,
        clientSearch: persistentFilters?.filters?.search,
        lotNumber: persistentFilters?.filters?.lotNumber,
        closingDateLocked: persistentFilters?.filters?.closingDateLocked,
        buildStatuses: selectedStatuses,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        showHiddenBuilds
      });
    },
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const history = useHistory();

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHiddenBuilds(!event.target.checked);
  };

  const handleEdit = (original: IUnderContract) => {
    history.push(`/sales/contracting/under-contract/${original.buildId}/contract-dates`);
  };

  const handleUpdateSorting = (payload: sortingPayload) => {
    dispatchSorting(updateSort(payload));
  };

  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      const directionOfSort = handleSortDirection(sortDirection[column]);
      handleUpdateSorting({
        sort: directionOfSort ? column : '',
        sortDirection: {
          [column]: directionOfSort
        },
        pageName: PAGE_NAME,
        environment: getEnvironment()
      });
    };

    return [
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContract } };
        }) => {
          return (
            <Box display="flex">
              <IconButton
                color="primary"
                title="Edit Contract"
                onClick={() => handleEdit(original)}
              >
                <Edit />
              </IconButton>
            </Box>
          );
        }
      },
      {
        Header: 'Sage ID',
        accessor: 'sageId',
        isServerSorted: selectedSort === 'SageId',
        isServerSortedDesc: sortDirection.SageId === 'Desc',
        handleClickColumn: () => handleClickColumn('SageId')
      },
      !isMobile && {
        Header: 'Neighborhood',
        accessor: 'neighborhoodName',
        isServerSorted: selectedSort === 'NeighborhoodName',
        isServerSortedDesc: sortDirection.NeighborhoodName === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodName'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContract } };
        }) => {
          return (
            <span>{`${original?.neighborhoodName ?? 'No Neighborhood Name'} - Phase ${
              original?.phase ?? 'No Phase'
            }`}</span>
          );
        }
      },
      !isMobile && {
        Header: 'Lot',
        accessor: 'lot',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Address',
        accessor: 'address',
        isServerSorted: selectedSort === 'StreetAddress',
        isServerSortedDesc: sortDirection.StreetAddress === 'Desc',
        handleClickColumn: () => handleClickColumn('StreetAddress')
      },
      !isMobile && {
        Header: 'Client Name',
        accessor: 'clientName',
        isServerSorted: selectedSort === 'ClientName',
        isServerSortedDesc: sortDirection.ClientName === 'Desc',
        handleClickColumn: () => handleClickColumn('ClientName')
      },
      {
        Header: 'Lender Name',
        accessor: 'lenderName',
        sort: false
      },
      {
        Header: 'Lender Contact',
        accessor: 'lenderContactName',
        sort: false
      },
      {
        Header: 'Loan Type',
        accessor: 'loanType',
        isServerSorted: selectedSort === 'LoanType',
        isServerSortedDesc: sortDirection.LoanType === 'Desc',
        handleClickColumn: () => handleClickColumn('LoanType')
      },
      {
        Header: 'Sage Closing Date',
        accessor: 'estimatedClosingDate',
        isServerSorted: selectedSort === 'EstimatedClosingDate',
        isServerSortedDesc: sortDirection.EstimatedClosingDate === 'Desc',
        handleClickColumn: () => handleClickColumn('EstimatedClosingDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContract } };
        }) => {
          return <span>{`${formatDate(original?.estimatedClosingDate) ?? ''}`}</span>;
        }
      },
      {
        Header: 'Actual Closing Date',
        accessor: 'actualClosingDate',
        isServerSorted: selectedSort === 'ActualClosingDate',
        isServerSortedDesc: sortDirection.ActualClosingDate === 'Desc',
        handleClickColumn: () => handleClickColumn('ActualClosingDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContract } };
        }) => {
          return (
            <span>{`${
              original?.isClosingAppointmentAllDay
                ? formatDate(original?.closingAppointmentStart) ?? ''
                : formatShortFriendlyDateWithTime(original?.closingAppointmentStart) ?? ''
            }`}</span>
          );
        }
      },
      {
        Header: 'Closing Notes',
        accessor: 'closingNotes',
        isServerSorted: selectedSort === 'ClosingNotes',
        isServerSortedDesc: sortDirection.ClosingNotes === 'Desc',
        handleClickColumn: () => handleClickColumn('ClosingNotes'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContract } };
        }) => {
          return <ClosingNotes notes={original.closingNotes} />;
        }
      },
      {
        Header: 'Homeowner Orientation',
        accessor: 'homeownerOrientation',
        isServerSorted: selectedSort === 'HomeownerOrientation',
        isServerSortedDesc: sortDirection.HomeownerOrientation === 'Desc',
        handleClickColumn: () => handleClickColumn('HomeownerOrientation'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContract } };
        }) => {
          return (
            <span>{`${
              formatShortFriendlyDateWithTime(original?.homeownerOrientation) ?? ''
            }`}</span>
          );
        }
      },
      {
        Header: 'Homeowner Celebration',
        accessor: 'homeownerCelebration',
        isServerSorted: selectedSort === 'HomeownerCelebration',
        isServerSortedDesc: sortDirection.HomeownerCelebration === 'Desc',
        handleClickColumn: () => handleClickColumn('HomeownerCelebration'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContract } };
        }) => {
          return (
            <span>{`${
              formatShortFriendlyDateWithTime(original?.homeownerCelebration) ?? ''
            }`}</span>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort, persistentFilters.filters, isMobile]);

  const data = useMemo(() => underContractData?.records ?? [], [underContractData]);

  const handleUpdateFilters = (payload: filtersPayload) => {
    dispatchFilters(updateFilters(payload));
  };

  return (
    <>
      <UnderContractFilters
        isLoading={isLoading}
        selectedNeighborhoodId={selectedNeighborhoodIds}
        selectedLotId={selectedLotId}
        setSelectedNeighborhoodId={setSelectedNeighborhoodIds}
        closingDateLocked={closingDateLocked}
        setClosingDateLocked={setClosingDateLocked}
        setSelectedLotId={setSelectedLotId}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setPage={setPage}
        handleSwitchChange={handleSwitchChange}
        handleSearch={(val: string) => {
          setPage(0);
          setSearchedValue(val);
          const payload = {
            ...persistentFilters,
            filters: {
              ...persistentFilters.filters,
              search: val
            },
            pageName: PAGE_NAME,
            environment: getEnvironment()
          };
          handleUpdateFilters(payload);
        }}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            const payload = {
              ...persistentFilters,
              filters: {
                search: '',
                neighborhoodIds: [],
                lotNumber: '',
                closingDateLocked: undefined,
                statuses: []
              },
              pageName: PAGE_NAME,
              environment: getEnvironment()
            };
            handleUpdateFilters(payload);
            setSearchValue('');
          } else {
            setPage(0);
            setSearchedValue(searchValue);
            const payload = {
              ...persistentFilters,
              filters: {
                search: searchValue,
                neighborhoodIds: selectedNeighborhoodIds,
                lotNumber: selectedLotId,
                closingDateLocked: closingDateLocked,
                showHiddenBuilds: showHiddenBuilds,
                statuses: selectedStatuses
              },
              pageName: PAGE_NAME,
              environment: getEnvironment()
            };
            handleUpdateFilters(payload);
          }
        }}
        hasAppliedFiltersGlobal={Object.keys(persistentFilters).length !== 0}
        showHiddenBuilds={showHiddenBuilds}
        setShowHiddenBuilds={setShowHiddenBuilds}
      />
      <Table
        isUnderContractPage
        stickyHeaders={removeStickyHeader ? false : true}
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          getAccordionHeader: (original: IUnderContract) => {
            let neighborhood = original.neighborhoodName;
            let phase = original.phase;
            let lot = original.lot;
            let contractName = original.clientName;
            let status = original.buildStatusName;
            return (
              <>
                {neighborhood} - Phase {phase} - Lot {lot}
                {contractName && (
                  <>
                    <br />
                    <Typography color={theme.palette.grey[800]} variant="caption">
                      {contractName}
                    </Typography>
                  </>
                )}
                {status && (
                  <Box>
                    <Typography
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
                        color: theme.palette.common.white,
                        borderRadius: theme.spacing(0.25)
                      }}
                      variant="caption"
                    >
                      {status}
                    </Typography>
                  </Box>
                )}
              </>
            );
          },
          handleEdit
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />

      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
    </>
  );
};
interface INotes {
  notes: string;
}

const ClosingNotes: FC<INotes> = ({ notes }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onPopperOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const onPopperClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <TablePopover anchorEl={anchorEl} text={notes} onPopperClose={onPopperClose} />
      <div
        aria-owns={!!anchorEl ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={e => {
          if (isTextOverflowing(notes)) {
            onPopperOpen(e);
          }
        }}
        onMouseLeave={onPopperClose}
      >{`${formatEllipsis(notes) ?? ''}`}</div>
    </>
  );
};
